import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Input, Modal, Segmented, Select, Spin, Switch, Table, message } from "antd";
import { Github, KeyRound, Plus, ShieldCheck, XCircle } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import {
  MonoCell,
  RowActionMenu,
  StatusPill,
  anTableProps,
  tableEmpty,
} from "../../../design-system/tableUi";
import { CopyButton, FieldLabel, InlineValue, Note } from "../ssh-proxy/sshProxyUi";
import { SettingsField, SettingsPage, SettingsSection } from "../../../components/settings/settingsUi";
import { isAdminUser } from "../../../utils/helpers";
import { DOMAIN_ID } from "../../../constants";
import {
  deleteSsoProvider,
  listSsoProviders,
  saveSsoProvider,
  ssoCallbackUrl,
  ssoError,
} from "../../../service/ssoService";

// Settings → Single sign-on: the OpenID Connect / GitHub providers this
// tenant's sign-in page offers.
//
// Backend: internal/pam/handler/instances/sso.go + internal/sso/provider.go.
// SSO only proves who someone is: a verified email is mapped to an existing
// active user of the tenant, never creates one. A provider shows on the
// sign-in page only while it is enabled and has a stored client secret.
//
// Validation mirrors sso.normalise so the form catches what the server would
// refuse with a 400; the server's message is still shown when it does.

const MASKED = "********";
const KINDS = [
  { value: "github", label: "GitHub" },
  { value: "oidc", label: "OpenID Connect" },
];
const KIND_NAME = { github: "GitHub", oidc: "OpenID Connect" };
const OIDC_DEFAULT_SCOPES = ["openid", "email", "profile"];

// sso.plainName: letters, digits, "-", "_", "." within a length.
const plainName = (s, max) => !!s && s.length <= max && /^[A-Za-z0-9._-]+$/.test(s);

const blankDraft = (kind = "github") => ({
  id: "",
  kind,
  name: kind === "github" ? "GitHub" : "",
  issuer: "",
  clientId: "",
  clientSecret: "",
  scopes: OIDC_DEFAULT_SCOPES,
  emailClaim: "email",
  requireEmailVerified: true,
  allowedDomains: [],
  githubOrg: "",
  enabled: true,
});

// Never prefill the stored secret: the field stays empty and "Stored" shows as
// its placeholder; an empty secret on an update keeps the stored one.
const draftFrom = (p) => ({
  id: p.id,
  kind: p.kind,
  name: p.name || "",
  issuer: p.issuer || "",
  clientId: p.clientId || "",
  clientSecret: "",
  scopes: p.kind === "oidc" && p.scopes?.length ? p.scopes : OIDC_DEFAULT_SCOPES,
  emailClaim: p.emailClaim || "email",
  requireEmailVerified: p.requireEmailVerified !== false,
  allowedDomains: p.allowedDomains || [],
  githubOrg: p.githubOrg || "",
  enabled: p.enabled !== false,
  hasSecret: !!p.hasSecret,
});

const normDomain = (d) => String(d || "").trim().replace(/^@/, "").toLowerCase();

function validate(d) {
  const e = {};
  const name = d.name.trim();
  if (!name || name.length > 60) e.name = "A name of up to 60 characters — it is shown on the sign-in button.";
  if (d.kind === "oidc") {
    let ok = false;
    try {
      const u = new URL(d.issuer.trim().replace(/\/+$/, ""));
      ok = u.protocol === "https:" && !!u.host && !u.search && !u.hash;
    } catch (_) {
      ok = false;
    }
    if (!ok) e.issuer = "The issuer URL (https), e.g. https://accounts.google.com";
    const claim = d.emailClaim.trim() || "email";
    if (!plainName(claim, 64)) e.emailClaim = "A plain claim name, e.g. email or preferred_username.";
    const bad = d.scopes.map((s) => s.trim()).filter((s) => s && !plainName(s.replace(/:/g, "."), 64));
    if (bad.length) e.scopes = `Not a valid scope: ${bad.join(", ")}`;
  } else if (d.githubOrg.trim() && !plainName(d.githubOrg.trim(), 39)) {
    e.githubOrg = "The organisation's login name, e.g. acme-corp.";
  }
  const cid = d.clientId.trim();
  if (!cid || cid.length > 255) e.clientId = "Required.";
  const secret = d.clientSecret.trim();
  if (!d.id && (!secret || secret === MASKED)) e.clientSecret = "Required.";
  const badDomains = d.allowedDomains.map(normDomain).filter((x) => x && (!plainName(x, 253) || !x.includes(".")));
  if (badDomains.length) e.allowedDomains = `Plain domain names, e.g. example.com (not ${badDomains.join(", ")})`;
  return e;
}

const buildBody = (d) => {
  const oidc = d.kind === "oidc";
  const body = {
    tenantId: DOMAIN_ID(),
    kind: d.kind,
    name: d.name.trim(),
    issuer: oidc ? d.issuer.trim().replace(/\/+$/, "") : "",
    clientId: d.clientId.trim(),
    clientSecret: d.clientSecret.trim(),
    // GitHub: [] → the server's defaults (read:user user:email, + read:org with an org).
    scopes: oidc ? d.scopes.map((s) => s.trim()).filter(Boolean) : [],
    emailClaim: oidc ? d.emailClaim.trim() || "email" : "",
    requireEmailVerified: oidc ? !!d.requireEmailVerified : true,
    allowedDomains: [...new Set(d.allowedDomains.map(normDomain).filter(Boolean))],
    githubOrg: oidc ? "" : d.githubOrg.trim(),
    disabled: !d.enabled,
  };
  if (d.id) body.id = d.id;
  return body;
};

function KindCell({ kind }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, color: AN_COLORS.ink2 }}>
      {kind === "github" ? <Github size={15} /> : <KeyRound size={15} />}
      {KIND_NAME[kind] || kind}
    </span>
  );
}

const muted = (s = "—") => <span style={{ color: AN_COLORS.muted }}>{s}</span>;

function ErrorText({ children }) {
  if (!children) return null;
  return <div style={{ color: AN_COLORS.danger, fontSize: 12, marginTop: 5 }}>{children}</div>;
}

function ProviderForm({ open, initial, onClose, onSaved }) {
  const [draft, setDraft] = useState(initial);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    if (open) {
      setDraft(initial);
      setTouched(false);
      setServerError("");
    }
  }, [open, initial]);

  const set = (patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    setServerError("");
  };
  const allErrors = useMemo(() => validate(draft), [draft]);
  const errors = touched ? allErrors : {};
  const editing = !!draft.id;
  const oidc = draft.kind === "oidc";

  const save = () => {
    setTouched(true);
    if (Object.keys(allErrors).length) return;
    setSaving(true);
    setServerError("");
    saveSsoProvider(buildBody(draft))
      .then((data) => onSaved(data?.provider, editing))
      .catch((err) => setServerError(ssoError(err, "Could not save the provider")))
      .finally(() => setSaving(false));
  };

  const field = { marginBottom: 14 };
  return (
    <Modal
      open={open}
      onCancel={saving ? undefined : onClose}
      title={editing ? `Edit ${initial.name || "provider"}` : "Add a sign-in provider"}
      width={620}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose} disabled={saving}>Cancel</Button>,
        <Button key="save" type="primary" loading={saving} onClick={save}>
          {editing ? "Save changes" : "Add provider"}
        </Button>,
      ]}
    >
      <div style={{ fontFamily: AN_FONT, paddingTop: 6 }}>
        <div style={field}>
          <FieldLabel>Provider type</FieldLabel>
          <Segmented
            value={draft.kind}
            options={KINDS}
            onChange={(kind) =>
              set({ kind, name: !editing && (!draft.name || draft.name === "GitHub") ? (kind === "github" ? "GitHub" : "") : draft.name })
            }
          />
        </div>

        <div style={field}>
          <FieldLabel required hint="shown on the sign-in button">Name</FieldLabel>
          <Input value={draft.name} maxLength={60} placeholder={oidc ? "e.g. Okta, Google, Entra ID" : "GitHub"}
            status={errors.name ? "error" : undefined} onChange={(e) => set({ name: e.target.value })} />
          <ErrorText>{errors.name}</ErrorText>
        </div>

        {oidc && (
          <div style={field}>
            <FieldLabel required>Issuer URL</FieldLabel>
            <Input value={draft.issuer} placeholder="https://accounts.google.com" style={{ fontFamily: AN_MONO }}
              status={errors.issuer ? "error" : undefined} onChange={(e) => set({ issuer: e.target.value })} />
            <ErrorText>{errors.issuer}</ErrorText>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={field}>
            <FieldLabel required>Client ID</FieldLabel>
            <Input value={draft.clientId} maxLength={255} style={{ fontFamily: AN_MONO }}
              status={errors.clientId ? "error" : undefined} onChange={(e) => set({ clientId: e.target.value })} />
            <ErrorText>{errors.clientId}</ErrorText>
          </div>
          <div style={field}>
            <FieldLabel required={!editing} hint={editing && draft.hasSecret ? "leave empty to keep" : undefined}>Client secret</FieldLabel>
            <Input.Password value={draft.clientSecret} autoComplete="new-password"
              placeholder={editing && draft.hasSecret ? "Stored" : ""}
              status={errors.clientSecret ? "error" : undefined} onChange={(e) => set({ clientSecret: e.target.value })} />
            <ErrorText>{errors.clientSecret}</ErrorText>
          </div>
        </div>

        {!oidc && (
          <div style={field}>
            <FieldLabel hint="optional — only its active members can sign in">GitHub organisation</FieldLabel>
            <Input value={draft.githubOrg} maxLength={39} placeholder="acme-corp" style={{ fontFamily: AN_MONO }}
              status={errors.githubOrg ? "error" : undefined} onChange={(e) => set({ githubOrg: e.target.value })} />
            <ErrorText>{errors.githubOrg}</ErrorText>
          </div>
        )}

        {oidc && (
          <>
            <div style={field}>
              <FieldLabel hint="openid is always requested">Scopes</FieldLabel>
              <Select mode="tags" value={draft.scopes} tokenSeparators={[",", " "]} open={false}
                style={{ width: "100%" }} status={errors.scopes ? "error" : undefined}
                onChange={(scopes) => set({ scopes })} />
              <ErrorText>{errors.scopes}</ErrorText>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "start" }}>
              <div style={field}>
                <FieldLabel hint="ID token claim">Email claim</FieldLabel>
                <Input value={draft.emailClaim} placeholder="email" style={{ fontFamily: AN_MONO }}
                  status={errors.emailClaim ? "error" : undefined} onChange={(e) => set({ emailClaim: e.target.value })} />
                <ErrorText>{errors.emailClaim}</ErrorText>
              </div>
              <div style={{ ...field, paddingTop: 26 }}>
                <Checkbox checked={draft.requireEmailVerified} onChange={(e) => set({ requireEmailVerified: e.target.checked })}>
                  Require a verified email (<InlineValue>email_verified</InlineValue>)
                </Checkbox>
              </div>
            </div>
          </>
        )}

        <div style={field}>
          <FieldLabel hint="optional — empty allows any domain">Allowed email domains</FieldLabel>
          <Select mode="tags" value={draft.allowedDomains} tokenSeparators={[",", " "]} open={false}
            placeholder="example.com" style={{ width: "100%" }} status={errors.allowedDomains ? "error" : undefined}
            onChange={(allowedDomains) => set({ allowedDomains })} />
          <ErrorText>{errors.allowedDomains}</ErrorText>
        </div>

        <div style={{ ...field, display: "flex", alignItems: "center", gap: 10 }}>
          <Switch checked={draft.enabled} onChange={(enabled) => set({ enabled })} />
          <span style={{ fontSize: 13, color: AN_COLORS.ink2 }}>
            {draft.enabled ? "Enabled — shown on the sign-in page" : "Disabled — hidden from the sign-in page"}
          </span>
        </div>

        {serverError && (
          <Note tone="danger" icon={<XCircle size={15} />}>{serverError}</Note>
        )}
      </div>
    </Modal>
  );
}

export default function SsoProviders() {
  const admin = isAdminUser();
  const [msg, msgCtx] = message.useMessage();

  const [loading, setLoading] = useState(admin);
  const [loadError, setLoadError] = useState("");
  const [providers, setProviders] = useState(null);
  const [form, setForm] = useState({ open: false, initial: blankDraft() });
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setLoadError("");
    return listSsoProviders(DOMAIN_ID())
      .then((data) => setProviders(Array.isArray(data?.providers) ? data.providers : []))
      .catch((err) => setLoadError(ssoError(err, "Could not read the sign-in providers")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (admin) load();
  }, [admin, load]);

  const openNew = () => setForm({ open: true, initial: blankDraft(providers?.length ? "oidc" : "github") });
  const openEdit = (p) => setForm({ open: true, initial: draftFrom(p) });
  const closeForm = () => setForm((f) => ({ ...f, open: false }));

  const onSaved = (p, editing) => {
    closeForm();
    msg.success(editing ? `${p?.name || "Provider"} saved` : `${p?.name || "Provider"} added`);
    load();
  };

  const confirmDelete = () => {
    if (!toDelete) return;
    setDeleting(true);
    setDeleteError("");
    deleteSsoProvider(toDelete.id)
      .then(() => {
        msg.success(`${toDelete.name} removed`);
        setToDelete(null);
        load();
      })
      .catch((err) => setDeleteError(ssoError(err, "Could not delete the provider")))
      .finally(() => setDeleting(false));
  };

  const columns = [
    {
      title: "Name", dataIndex: "name", key: "name",
      render: (v) => <span style={{ fontWeight: 500, color: AN_COLORS.ink }}>{v}</span>,
    },
    { title: "Type", dataIndex: "kind", key: "kind", render: (k) => <KindCell kind={k} /> },
    {
      title: "Issuer / organisation", key: "issuer",
      render: (_, p) =>
        p.kind === "oidc"
          ? <MonoCell title={p.issuer}>{p.issuer}</MonoCell>
          : p.githubOrg ? <MonoCell>{p.githubOrg}</MonoCell> : muted("Any GitHub account"),
    },
    { title: "Client ID", dataIndex: "clientId", key: "clientId", render: (v) => <MonoCell title={v}>{v}</MonoCell> },
    {
      title: "Allowed domains", dataIndex: "allowedDomains", key: "domains",
      render: (d) => (d?.length ? <span style={{ fontFamily: AN_MONO, fontSize: 12 }}>{d.join(", ")}</span> : muted("Any")),
    },
    {
      title: "Status", key: "status",
      render: (_, p) =>
        !p.enabled
          ? <StatusPill status="disabled" />
          : p.hasSecret
            ? <StatusPill status="enabled" />
            : <StatusPill status="warning" label="No client secret" />,
    },
    {
      title: "", key: "actions", width: 56, align: "right",
      render: (_, p) => (
        <RowActionMenu
          items={[
            { key: "edit", label: "Edit", onClick: () => openEdit(p) },
            { type: "divider" },
            { key: "delete", danger: true, label: "Delete", onClick: () => { setDeleteError(""); setToDelete(p); } },
          ]}
        />
      ),
    },
  ];

  const title = "Single sign-on";
  const description =
    "Let people sign in to this tenant with GitHub or an OpenID Connect provider (Google, Okta, Entra ID, Keycloak…). The verified email must belong to an existing active user — SSO never creates accounts.";

  if (!admin) {
    return (
      <SettingsPage title={title} description={description}>
        <Note tone="info" icon={<ShieldCheck size={15} />}>
          Single sign-on providers can only be viewed and changed by an administrator.
        </Note>
      </SettingsPage>
    );
  }

  const callback = ssoCallbackUrl();

  return (
    <SettingsPage
      title={title}
      description={description}
      actions={
        <Button type="primary" onClick={openNew} disabled={!providers} icon={<Plus size={14} style={{ verticalAlign: -2 }} />}>
          Add provider
        </Button>
      }
    >
      {msgCtx}

      {loading && !providers ? (
        <div style={{ padding: 60, textAlign: "center" }}><Spin /></div>
      ) : loadError && !providers ? (
        <div style={{ display: "grid", gap: 12 }}>
          <Note tone="danger" icon={<XCircle size={15} />}>{loadError}</Note>
          <div><Button onClick={() => load()}>Try again</Button></div>
        </div>
      ) : (
        <>
          <SettingsSection
            title="Providers"
            description="A provider is offered on the sign-in page while it is enabled and has a stored client secret."
          >
            <Table
              {...anTableProps}
              rowKey="id"
              columns={columns}
              dataSource={providers || []}
              loading={loading}
              pagination={false}
              locale={tableEmpty(loading, "No sign-in providers yet — add GitHub or an OpenID Connect provider.")}
            />
          </SettingsSection>

          <SettingsSection
            title="Provider setup"
            description="Register this callback URL with the provider: as the authorization callback URL of a GitHub OAuth app, or as the redirect URI of an OpenID Connect client."
          >
            <SettingsField
              last
              label="Callback URL"
              help={
                <>
                  Derived from this deployment's API address. It must match the service's <InlineValue>SSO_CALLBACK_URL</InlineValue> (https),
                  and <InlineValue>SSO_CONSOLE_DOMAIN</InlineValue> must be set — without both, single sign-on stays off.
                </>
              }
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <InlineValue>{callback}</InlineValue>
                <CopyButton text={callback} />
              </div>
            </SettingsField>
          </SettingsSection>
        </>
      )}

      <ProviderForm open={form.open} initial={form.initial} onClose={closeForm} onSaved={onSaved} />

      <Modal
        open={!!toDelete}
        title={`Delete ${toDelete?.name || "provider"}?`}
        onCancel={deleting ? undefined : () => setToDelete(null)}
        footer={[
          <Button key="cancel" onClick={() => setToDelete(null)} disabled={deleting}>Cancel</Button>,
          <Button key="delete" danger type="primary" loading={deleting} onClick={confirmDelete}>Delete</Button>,
        ]}
      >
        <div style={{ display: "grid", gap: 12, fontFamily: AN_FONT, fontSize: 13, color: AN_COLORS.ink2 }}>
          <div>
            The “Continue with {toDelete?.name}” button disappears from the sign-in page and its stored client secret is retired.
            People can still sign in any other way they normally do.
          </div>
          {deleteError && <Note tone="danger" icon={<XCircle size={15} />}>{deleteError}</Note>}
        </div>
      </Modal>
    </SettingsPage>
  );
}
